import React from "react";
import { useState, useRef, useEffect } from "react";
import {
    Box, Typography, Chip, Button, IconButton, Grid, TextField, Stack, Popper, Grow, Paper, ClickAwayListener, Tabs, Tab,
    Menu, MenuItem, Collapse
} from "@mui/material";
import moment from "moment";
import TableComponent from "components/grid";
import StatusBadge from "components/badge/StatusBadge";
import CustomPagination from "components/CustomPagination";
import { ReactComponent as EditIcon } from "assets/icons/EditIcon.svg";
import { LuEye, LuRefreshCw, LuSearch } from "react-icons/lu";
import { BiListUl } from "react-icons/bi";
import { MdOutlineFileDownload, MdOutlineFilterAlt, MdDeleteOutline } from "react-icons/md";
import { FaPlus } from "react-icons/fa6";
import Select from "react-select";
import CustomButton from "components/Button";
import AddEHSFinding from "./AddEHSFinding";
import EditEHSFinding from "./EditEHSFinding";
import ViewEHSFinding from "./ViewEHSFinding";
import DeleteEHSFinding from "./DeleteEHSFinding";
import { useLazyGetEHSFindingListQuery, useLazyGetExportEHSFindingQuery } from "store/slices/apis/ehsFindingApiSlice";
import { useParams } from "react-router-dom";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CalenderSVG from "assets/logo/Calendar";
import { ptwIssuedOptions, stopWorkOptions } from "helpers/ehsFindingOptions";
import { useGetMasterDataQuery } from "store/slices/apis/masterConfigation";
import { mapAndSortAlphabetically } from "utils/mapSortHelper";
import { EHS_FINDING_TRACKER_CREATE, EHS_FINDING_TRACKER_DELETE, EHS_FINDING_TRACKER_EDIT, EHS_FINDING_TRACKER_VIEW } from "helpers/permissionConfig";
import { usePermission } from "hooks/usePermission";
import ImageSlider from "./ImageSlider";
import { exportToExcel } from "utils/ExportToExcel";
import { closeSnackbar, openSnackbar } from "store/slices/snackbar";
import { DEFAULT_DATE_FORMAT } from "config";
import { useDispatch } from "store";
import { useGetLastUpdatedOnStatusQuery } from "store/slices/apis/projectStatusApiSlice";
import ProjectLastUpdatedOn from "components/ProjectLastUpdatedOn";
import TrackerPageHeader from "components/TrackerPageHeader";


const normalize = (value) =>
    (value || "")
        .toString()
        .trim()
        .toUpperCase();

const ncrStatusOptions = [
    { label: "Open", value: "Open" },
    { label: "Closed", value: "Closed" },
];

const totalCount = 0;
const openCount = 0;
const closedCount = 0;

const EHSFindingTracker = () => {
    const [showSummary, setShowSummary] = useState(true);
    const dispatch = useDispatch()

    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuRow, setMenuRow] = useState(null);

    const [searchTerm, setSearchTerm] = useState("");

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleMenuOpen = (event, row) => {
        event.stopPropagation(); // prevent row click if any
        setMenuAnchorEl(event.currentTarget);
        setMenuRow(row);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuRow(null);
    };

    const handleMenuView = () => {
        if (menuRow) {
            handleViewClick(menuRow);
        }
        handleMenuClose();
    };

    const handleMenuEdit = () => {
        if (menuRow) {
            handleEditEHS(menuRow);
        }
        handleMenuClose();
    };
    const { projectId } = useParams();

    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sortOrder, setSortOrder] = useState('desc');
    const [sortBy, setSortBy] = useState('CreatedDate');

    const [open, setOpen] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const anchorRef = useRef(null);
    const [isAddEHS, setIsAddEHS] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedData, setSelectedData] = useState(null);
    const [isView, setIsView] = useState(false);
    const [viewData, setViewData] = useState(null);
    const [isDelete, setIsDelete] = useState(false);
    const [deleteData, setDeleteData] = useState(null);
    const [files, setFiles] = useState([]);
    const [title, setTitle] = useState(null);
    const [show, setShow] = useState(false);

    const createAccess = usePermission(EHS_FINDING_TRACKER_CREATE);
    const editAccess = usePermission(EHS_FINDING_TRACKER_EDIT);
    const viewAccess = usePermission(EHS_FINDING_TRACKER_VIEW);
    const deleteAccess = usePermission(EHS_FINDING_TRACKER_DELETE);

    const [filters, setFilters] = useState({
        SortBy: sortBy,
        SortOrder: sortOrder,
        PageNumber: pageNumber,
        PageSize: pageSize,
        ProjectId: projectId,
        SearchTerm: "",
        StopWorkRecommended: null,
    });

    const module = "EHSFinding";

    const { data: ehsContractorsRes } = useGetMasterDataQuery({ module: module, category: 'ContractorInvolved', ProjectId: projectId });
    const contractorData = ehsContractorsRes?.data || [];
    const contractorInvolvedOptions = mapAndSortAlphabetically(contractorData);

    const { data: statusData } = useGetMasterDataQuery({ module: module, category: 'StatusofCompliance', ProjectId: projectId });
    const { data: reportedByData } = useGetMasterDataQuery({ module: module, category: 'ReportedBy', ProjectId: projectId });
    const { data: categoryData } = useGetMasterDataQuery({ module: module, category: 'Category', ProjectId: projectId });
    const { data: actionOwnerData } = useGetMasterDataQuery({ module: module, category: 'ActionOwner', ProjectId: projectId });
    const { data: verifiedByData } = useGetMasterDataQuery({ module: module, category: 'VerifiedBy', ProjectId: projectId });
    const { data: severityData } = useGetMasterDataQuery({ module: module, category: 'Severity', ProjectId: projectId });

    const statusOfComplianceOptions = statusData?.data?.map((item) => ({
        label: item?.value,
        value: item?.value
    })) || [];

    const reportedByOptions = reportedByData?.data?.map((item) => ({
        label: item?.value,
        value: item?.value
    })) || [];


    const categoryOptions = categoryData?.data?.map((item) => ({
        label: item?.value,
        value: item?.value
    })) || [];

    const actionOwnerOptions = actionOwnerData?.data?.map((item) => ({
        label: item?.value,
        value: item?.value
    })) || [];

    const verifiedByOptions = verifiedByData?.data?.map((item) => ({
        label: item?.value,
        value: item?.value
    })) || [];

    const severityOptions = severityData?.data?.map((item) => ({
        label: item?.value,
        value: item?.value
    })) || [];


    const [trigger, { data: ehsFindingList, isFetching }] = useLazyGetEHSFindingListQuery();
    const { data: ehsFindingData } = useGetLastUpdatedOnStatusQuery({ ProjectId: projectId });



    useEffect(() => {
        trigger(filters);
    }, []);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchTerm !== filters.SearchTerm) {
                const updatedFilters = {
                    ...filters,
                    SearchTerm: searchTerm,
                    PageNumber: 1,
                };
                setFilters(updatedFilters);
                trigger(updatedFilters);
                setPageNumber(1);
            }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    const handlePageChange = (newPage) => {
        setPageNumber(newPage);
        const updatedFilters = {
            ...filters,
            PageNumber: newPage,
        };
        setFilters(updatedFilters);
        trigger(updatedFilters);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPageNumber(1);
        const updatedFilters = {
            ...filters,
            PageSize: newSize,
            PageNumber: 1,
        };
        setFilters(updatedFilters);
        trigger(updatedFilters);
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);

        const updatedFilters = {
            ...filters,
            PageNumber: 1,
            ...(newValue > 0 ? { NCRStatus: ncrStatusOptions[newValue - 1]?.value } : {}),
        };

        if (newValue === 0 && updatedFilters.NCRStatus) {
            delete updatedFilters.NCRStatus;
        }

        setFilters(updatedFilters);
        trigger(updatedFilters);
        setPageNumber(1);
    };

    const handleApplyFilters = () => {
        const updatedFilters = {
            ...filters,
            PageNumber: 1,
        };

        setFilters(updatedFilters);
        trigger(updatedFilters);
        setPageNumber(1);
        handleClose();
    };

    const handleReset = () => {
        const resetFilters = {
            ProjectId: projectId,
            SortBy: sortBy,
            SortOrder: sortOrder,
            PageNumber: 1,
            PageSize: 10,
            SearchTerm: "",
            StopWorkRecommended: null,
        };
        setFilters(resetFilters);
        trigger(resetFilters);
        setPageNumber(1);
        handleClose();
    };

    const handleSelectChange = (selectedOption, fieldName) => {
        const updatedFilters = {
            ...filters,
            [fieldName]: selectedOption?.value ?? null,
            PageNumber: 1,
        };

        setFilters(updatedFilters);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        const updatedFilters = {
            ...filters,
            [name]: value ? `${value}T00:00:00Z` : "",
            PageNumber: 1,
        };

        setFilters(updatedFilters);
    };

    const handleRequestSort = (property) => {
        const isAsc = sortBy === property && sortOrder === 'asc';
        const updatedFilters = {
            ...filters,
            SortBy: property,
            SortOrder: isAsc ? 'desc' : 'asc'
        };
        setFilters(updatedFilters);
        setSortOrder(isAsc ? 'desc' : 'asc');
        setSortBy(property);
        trigger(updatedFilters);
    };

    const handleDelete = (filterType) => {
        const updatedFilters = {
            ...filters,
            [filterType]: "",
            PageNumber: 1
        };

        setFilters(updatedFilters);
        trigger(updatedFilters);
        setPageNumber(1);
    };

    const handleAddEHS = () => {
        setIsAddEHS((prev) => !prev);
    };

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleEditEHS = (row) => {
        setIsEdit((prev) => !prev);
        setSelectedData(row);
    };

    const handleEditEHSClose = () => {
        setIsEdit((prev) => !prev);
        setSelectedData(null);
    };

    const handleViewClick = (row) => {
        setIsView((prev) => !prev);
        setViewData(row);
    };

    const handleViewClose = () => {
        setIsView(false);
        setViewData(null);
    };

    const handleDeleteClick = (row) => {
        setIsDelete((prev) => !prev);
        setDeleteData(row);
    };

    const handleDeleteClose = () => {
        setIsDelete(false);
        setDeleteData(null);
    };

    const handleMenuDelete = () => {
        if (menuRow) {
            handleDeleteClick(menuRow);
        }
        handleMenuClose();
    };

    const handleToggleSummary = () => {
        setShowSummary((prev) => !prev);
    };

    const highlightText = (text, highlight) => {
        if (!highlight || !text) return text;

        const regex = new RegExp(`(${highlight})`, 'gi');
        const parts = String(text).split(regex);

        return parts.map((part, index) =>
            regex.test(part) ? (
                <span
                    key={index}
                    style={{
                        backgroundColor: '#FFF3CD',
                        color: '#856404',
                        fontWeight: 'bold',
                        padding: '0 2px',
                        borderRadius: '3px'
                    }}
                >
                    {part}
                </span>
            ) : part
        );
    };

    const handlePhotographClick = (images, heading) => {
        setFiles(images);
        setTitle(heading);
        setShow(true);
    }

    const handlePhotographClose = () => {
        setFiles([]);
        setTitle(null);
        setShow(false)
    }

    const openNotification = (msg, type = "error", duration = 3000) => {
        dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: 'alert',
                alert: {
                    color: type
                },
                close: false
            })
        );
        setTimeout(() => dispatch(closeSnackbar()), duration);
    };

    const ALL_EXPORT_COLUMNS = [

        { name: "Date", field: "dateTime" },
        { name: "EHS Finding", field: "ehsFinding" },
        { name: "Severity", field: "severity" },
        { name: "Finding Photographs", field: "findingPhotographs" },
        { name: "Reported By", field: "reportedBy" },
        { name: "Recommendation", field: "recommendation" },
        { name: "Area", field: "area" },
        { name: "Location", field: "location" },
        { name: "Contractor & Sub Contractor Involved", field: "contractorSubContractorInvolved" },
        { name: "Category", field: "category" },
        { name: "PTW Issued", field: "ptwIssued" },
        { name: "PTW Issued By", field: "ptwIssuedBy" },
        { name: "Stop Work Recommended", field: "stopWorkRecommended" },
        { name: "Target Closure Date", field: "targetClosureDate" },
        { name: "Action Owner", field: "actionOwner" },
        { name: "Compliance Statement", field: "complianceStatement" },
        { name: "Compliance Photographs", field: "compliancePhotographs" },
        { name: "Verified By", field: "verifiedBy" },
        { name: "Status Of Compliance", field: "statusOfCompliance" },
        { name: "Ageing", field: "ageing" },

    ];


    const [triggerExport, { isFetching: isExporting }] = useLazyGetExportEHSFindingQuery();

    const handleExportExcel = async () => {
        try {
            const exportFilters = {
                ...filters,
                PageNumber: 1,
                PageSize: 5000,
            };

            const response = await triggerExport(exportFilters).unwrap();
            const rows = response?.data || [];

            if (!Array.isArray(rows) || rows.length === 0) {
                openNotification("No data available to export", "warning");
                return;
            }

            const isDateField = (field) => {
                const dateFields = ["dateTime", "targetClosureDate"];
                return dateFields.includes(field);
            };

            const formattedData = rows.map((row, index) => {
                const rowData = { "S.No": index + 1 };

                ALL_EXPORT_COLUMNS.forEach(col => {
                    const field = col.field;
                    let value = "N/A";
                    const raw = row[field];


                    if (field === "findingPhotographs" || field === "compliancePhotographs") {
                        value = raw?.length
                            ? raw.map(img => img.filePath).join(", ")
                            : "N/A";
                    }


                    else if (field === "ptwIssued" || field === "stopWorkRecommended") {
                        value = raw === true ? "Yes" : raw === false ? "No" : "N/A";
                    }


                    else if (isDateField(field) && raw && moment(raw).isValid()) {
                        value = moment.utc(raw).format("DD MMM YYYY");
                    }


                    else {
                        value = raw ?? "N/A";
                    }

                    rowData[col.name] = value;
                });

                return rowData;
            });

            exportToExcel(formattedData, "EHS_Findings_Data", "EHS Findings");

            openNotification("Excel exported successfully", "success");
        } catch (error) {
            console.error("Export error:", error);
            openNotification("Failed to export Excel", "error");
        }
    };



    const columns = [
        {
            name: "",
            field: "actions",
            width: "10px",
            sticky: true,
            renderer: (row) => (
                <>
                    {(viewAccess || editAccess || deleteAccess) &&
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <IconButton
                                size="small"
                                onClick={(e) => handleMenuOpen(e, row)}
                            >
                                <MoreVertIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    }
                </>
            ),
        },
        {
            name: "ID",
            field: "customId",
            width: "100px",
            sticky: true,
        },
        {
            name: "Date",
            field: "dateTime",
            width: "120px",
            sticky: true,
            renderer: (row) => (
                row?.dateTime ? moment.utc(row.dateTime).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "EHS Finding",
            field: "ehsFinding",
            width: "300px",
            sticky: true,
            renderer: (row) => highlightText(row.ehsFinding, searchTerm),
        },
        {
            name: "Severity",
            field: "severity",
            renderer: (row) => row?.severity || "N/A",
        },
        {
            name: "Finding Photographs",
            field: "findingPhotographs",
            renderer: (row) => {
                const images = row?.findingPhotographs || [];
                return (
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                        {images.length > 0 ? (
                            images.slice(0, 1).map((file, index) => (
                                <Box key={index}>
                                    <img
                                        src={file?.filePath}
                                        alt="img"
                                        width={80}
                                        height={80}
                                        style={{
                                            objectFit: "cover",
                                            borderRadius: "3px",
                                            border: "1px solid #ddd",
                                            cursor: "pointer"
                                        }}
                                        onClick={() => handlePhotographClick(images, "Finding Photographs")}
                                    />
                                </Box>
                            ))
                        ) : (
                            <Typography variant="caption">N/A</Typography>
                        )}
                    </Box>
                );
            }
        },
        {
            name: "Reported By",
            field: "reportedBy",
            renderer: (row) => row?.reportedBy || "N/A",
        },
        {
            name: "Recommendation",
            field: "recommendation",
            width: "300px",
            renderer: (row) => row?.recommendation || "N/A",
        },
        {
            name: "Area",
            field: "area",
            width: "150px",
            renderer: (row) => row?.area ? highlightText(row.area, searchTerm) : "N/A",
        },
        {
            name: "Location",
            field: "location",
            width: "150px",
            renderer: (row) => row?.location ? highlightText(row.location, searchTerm) : "N/A",
        },
        {
            name: "Contractor & Sub Contractor Involved",
            field: "contractorSubContractorInvolved",
            width: "200px",
            renderer: (row) => row?.contractorSubContractorInvolved ? highlightText(row.contractorSubContractorInvolved, searchTerm) : "N/A",
        },
        {
            name: "Category",
            field: "category",
            renderer: (row) => row?.category ? highlightText(row.category, searchTerm) : "N/A",
        },
        {
            name: "PTW Issued",
            field: "ptwIssued",
            renderer: (row) => {
                const val = row?.ptwIssued;

                if (val === true) return "Yes";
                if (val === false) return "No";
                return "N/A";

            }
        },
        {
            name: "PTW Issued By",
            field: "ptwIssuedBy",
            renderer: (row) => row?.ptwIssuedBy || "N/A",
        },
        {
            name: "Stop Work Recommended",
            field: "stopWorkRecommended",
            renderer: (row) => {
                const val = row?.stopWorkRecommended;

                if (val === true) return "Yes";
                if (val === false) return "No";
                return "N/A";
            }
        },
        {
            name: "Target Closure Date",
            field: "targetClosureDate",
            renderer: (row) => (
                row?.targetClosureDate ? moment.utc(row.targetClosureDate).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Action Owner",
            field: "actionOwner",
            renderer: (row) => row?.actionOwner ? highlightText(row.actionOwner, searchTerm) : "N/A",
        },
        {
            name: "Compliance Statement",
            field: "complianceStatement",
            width: "300px",
            renderer: (row) => row?.complianceStatement || "N/A",
        },
        {
            name: "Compliance Photographs",
            field: "compliancePhotographs",
            renderer: (row) => {
                const images = row?.compliancePhotographs || [];
                return (
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                        {images.length > 0 ? (
                            images.slice(0, 1).map((file, index) => (
                                <Box key={index}>
                                    <img
                                        src={file?.filePath}
                                        alt="img"
                                        width={80}
                                        height={80}
                                        style={{
                                            objectFit: "cover",
                                            borderRadius: "3px",
                                            border: "1px solid #ddd",
                                            cursor: "pointer"
                                        }}
                                        onClick={() => handlePhotographClick(images, "Compliance Photographs")}
                                    />
                                </Box>
                            ))
                        ) : (
                            <Typography variant="caption">N/A</Typography>
                        )}
                    </Box>
                );
            }
        },
        {
            name: "Verified By",
            field: "verifiedBy",
            renderer: (row) => row?.verifiedBy ? highlightText(row.verifiedBy, searchTerm) : "N/A",
        },
        {
            name: 'Status Of Compliance',
            field: 'statusOfCompliance',
            renderer: (row) => (
                row?.statusOfCompliance ? <StatusBadge status={row?.statusOfCompliance} /> : "N/A"
            )
        },
        {
            name: "Ageing ",
            field: "ageing",
            renderer: (row) => row?.ageing ?? "N/A",
        }
    ];

    return (
        <>
            <TrackerPageHeader title="EHS Finding Tracker" description="Track findings, corrective actions, severity, and compliance.">
                <ProjectLastUpdatedOn date={ehsFindingData?.ehsFindings} />
            </TrackerPageHeader>
            <Grid container spacing={2} sx={{ p: 2 }}>

                <Grid size={12}>
                    <Box
                        sx={{
                            width: "100%",
                            borderBottom: "2px solid #ECECEC",
                            overflowX: "auto",
                            whiteSpace: "nowrap",
                            p: 0,
                            display: "flex", alignItems: "center", justifyContent: 'space-between',
                            "-webkit-overflow-scrolling": "touch",
                        }}
                    >
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            TabIndicatorProps={{ style: { display: "none" } }}
                            variant="scrollable"
                            scrollButtons={false}
                            sx={{
                                justifyContent: "flex-start !important",
                                alignItems: "flex-start",
                                "& .MuiTabs-flexContainer": {
                                    justifyContent: "flex-start !important",
                                    gap: "30px",
                                },
                            }}
                        >
                            {["All"].map((label, index) => {
                                let count = 0;

                                if (label === "All") {
                                    count = totalCount;
                                }
                                else if (label === "Open") {
                                    count = openCount;
                                }
                                else if (label === "Closed") {
                                    count = closedCount;
                                }

                                return (
                                    <Tab
                                        key={index}
                                        label={
                                            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 500,
                                                        fontSize: "14px",
                                                        lineHeight: "100%",
                                                        color: tabValue === index ? "#0E73F6" : "#7E7E7E",
                                                        fontFamily: "Outfit, sans-serif"
                                                    }}
                                                >
                                                    {label}
                                                </Typography>
                                                {index > 0 &&
                                                    <Chip
                                                        label={count}
                                                        sx={{
                                                            height: "20px",
                                                            fontSize: "14px",
                                                            fontWeight: 500,
                                                            lineHeight: 1,
                                                            backgroundColor: "#E5E9EB",
                                                            color: "#5F5F5F",
                                                        }}
                                                    />
                                                }

                                            </Box>
                                        }
                                        sx={{
                                            textTransform: "none",
                                            minWidth: "auto",
                                            paddingBottom: "14px",
                                            paddingLeft: 0,
                                            paddingRight: 0,
                                            borderBottom: tabValue === index ? "2px solid #0E73F6" : "2px solid transparent",
                                        }}
                                    />
                                );
                            })}
                        </Tabs>
                        {createAccess &&
                            <CustomButton
                                label="Create EHS"
                                startIcon={<FaPlus />}
                                color="primary"
                                onClick={handleAddEHS}
                                sx={{
                                    backgroundColor: "#2563EB",
                                    fontFamily: 'Outfit',
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    lineHeight: '100%',
                                    letterSpacing: '0px',
                                }}
                            />
                        }
                    </Box>
                </Grid>

                <Grid size={12}>
                    <Grid container>
                        <Grid size={{ md: 8, sm: 6 }}>
                            <Stack
                                direction="row"
                                spacing={1}
                                flexWrap="wrap"
                                sx={{
                                    padding: "8px 0",
                                    alignItems: "center",
                                    minHeight: "40px"
                                }}
                            >

                                {filters?.ReportedBy &&
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Reported By:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>{filters?.ReportedBy}</span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('ReportedBy')}
                                    />
                                }

                                {filters.ContractorSubContractorInvolved && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Contractor:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {filters.ContractorSubContractorInvolved}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('ContractorSubContractorInvolved')}
                                    />
                                )}

                                {filters.Category && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Category:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {filters.Category}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('Category')}
                                    />
                                )}

                                {typeof filters.StopWorkRecommended === "boolean" && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Stop Work:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {filters.StopWorkRecommended ? "Yes" : "No"}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('StopWorkRecommended')}
                                    />
                                )}

                                {filters.DateTime && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Date:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {moment(filters.DateTime).format('DD-MM-YYYY')}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('DateTime')}
                                    />
                                )}

                                {filters.TargetClosureDate && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Closure Date:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {moment(filters.TargetClosureDate).format('DD-MM-YYYY')}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('TargetClosureDate')}
                                    />
                                )}
                                {filters.StatusOfCompliance && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Status of Compliance:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {filters.StatusOfCompliance}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('StatusOfCompliance')}
                                    />
                                )}
                                {
                                    filters.Severity && (
                                        <Chip
                                            label={
                                                <Typography variant="body2">
                                                    <strong style={{ color: '#0E73F6' }}>Severity:</strong>{" "}
                                                    <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                        {filters.Severity}
                                                    </span>
                                                </Typography>
                                            }
                                            sx={{
                                                backgroundColor: "rgba(25, 118, 210, 0.08)",
                                                border: 0
                                            }}
                                            variant="outlined"
                                            onDelete={() => handleDelete('Severity')}
                                        />
                                    )
                                }

                            </Stack>
                        </Grid>


                        <Grid size={{ md: 4, sm: 6 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: { md: "row", xs: "column" },
                                    alignItems: { md: "center", xs: "flex-start" },
                                    justifyContent: 'end',
                                    p: "8px 0px"
                                }}
                            >
                                <TextField
                                    placeholder="Search By EHS Finding, Area, Location, Status..."
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    variant="outlined"
                                    size="small"
                                    InputProps={{
                                        startAdornment: <LuSearch style={{ marginRight: '8px', color: '#6B7280' }} />,
                                    }}
                                    sx={{
                                        minWidth: { md: '320px', xs: "auto" },
                                        // width: "100%",
                                        backgroundColor: '#fff',
                                        '& .MuiOutlinedInput-root': { borderRadius: '3px', minHeight: '36px', fontSize: '13px' },
                                        '& .MuiInputBase-input::placeholder': {
                                            fontSize: '0.9rem',
                                        }
                                    }}
                                    type="search"
                                />

                                <IconButton
                                    ref={anchorRef}
                                    onClick={handleToggle}
                                >
                                    <MdOutlineFilterAlt size={24} color="#48535B" />
                                </IconButton>
                                <IconButton onClick={handleExportExcel} disabled={isExporting}>
                                    <MdOutlineFileDownload size={24} color="#48535B" />
                                </IconButton>

                                <Popper
                                    open={open}
                                    anchorEl={anchorRef.current}
                                    placement="bottom-end"
                                    transition
                                    disablePortal={false}
                                    sx={{ zIndex: 2000 }}
                                >
                                    {({ TransitionProps }) => (
                                        <Grow {...TransitionProps}>
                                            <Paper
                                                sx={{
                                                    mt: 1,
                                                    p: 2,
                                                    borderRadius: 1,
                                                    boxShadow: "0px 4px 20px rgba(7, 7, 7, 0.08)",
                                                    width: 600,
                                                }}
                                            >
                                                <ClickAwayListener onClickAway={handleClose}>
                                                    <Box>
                                                        <Grid container spacing={2}>
                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Select Status of Compliance</Typography>
                                                                <Select
                                                                    name="StatusOfCompliance"
                                                                    options={statusOfComplianceOptions}
                                                                    isClearable
                                                                    placeholder="Select Status of Compliance "
                                                                    onChange={(option) => handleSelectChange(option, "StatusOfCompliance")}
                                                                    value={statusOfComplianceOptions.find(option => option.value === filters.StatusOfCompliance) || null}
                                                                />
                                                            </Grid>
                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Severity</Typography>
                                                                <Select
                                                                    name="Severity"
                                                                    options={severityOptions}
                                                                    value={severityOptions.find(opt => opt.value === String(filters.Severity)) || null}
                                                                    onChange={(option) => handleSelectChange(option, "Severity")}
                                                                    isClearable
                                                                    placeholder="Select Severity"
                                                                />
                                                            </Grid>
                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Select Reported By</Typography>
                                                                <Select
                                                                    name="ReportedBy"
                                                                    options={reportedByOptions}
                                                                    isClearable
                                                                    placeholder="Select Reported By"
                                                                    onChange={(option) => handleSelectChange(option, "ReportedBy")}
                                                                    value={reportedByOptions.find(option => option.value === filters.ReportedBy) || null}
                                                                />
                                                            </Grid>

                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Select Contractor Involved</Typography>
                                                                <Select
                                                                    name="ContractorSubContractorInvolved"
                                                                    options={contractorInvolvedOptions}
                                                                    isClearable
                                                                    placeholder="Select Contractor Involved"
                                                                    onChange={(option) => handleSelectChange(option, "ContractorSubContractorInvolved")}
                                                                    value={contractorInvolvedOptions.find(option => option.value === filters.ContractorSubContractorInvolved) || null}
                                                                />
                                                            </Grid>

                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Select Category</Typography>
                                                                <Select
                                                                    name="Category"
                                                                    options={categoryOptions}
                                                                    isClearable
                                                                    placeholder="Select Category"
                                                                    onChange={(option) => handleSelectChange(option, "Category")}
                                                                    value={categoryOptions.find(option => option.value === filters.Category) || null}
                                                                />
                                                            </Grid>

                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Select Stop Work Recommended</Typography>
                                                                <Select
                                                                    name="StopWorkRecommended"
                                                                    options={stopWorkOptions}
                                                                    isClearable
                                                                    placeholder="Select Stop Work Recommended"
                                                                    onChange={(option) => handleSelectChange(option, "StopWorkRecommended")}
                                                                    value={stopWorkOptions.find(option => option.value === filters.StopWorkRecommended) || null}
                                                                />
                                                            </Grid>

                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Date</Typography>
                                                                <TextField
                                                                    type="date"
                                                                    name="DateTime"
                                                                    // onBlur={handleBlur}
                                                                    value={filters.DateTime ? new Date(filters.DateTime).toISOString().split('T')[0] : ""}
                                                                    onChange={handleInputChange}
                                                                    onClick={(e) => {
                                                                        // Trigger the native date picker
                                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                                        if (input) input.showPicker?.();
                                                                    }}
                                                                    fullWidth
                                                                    sx={{
                                                                        // ...rightTextStyle,
                                                                        "& input::-webkit-calendar-picker-indicator": {
                                                                            display: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& input[type='date']": {
                                                                            appearance: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& .MuiOutlinedInput-root": {
                                                                            "& fieldset": {
                                                                                border: "none",
                                                                            },
                                                                        },
                                                                    }}
                                                                    InputProps={{
                                                                        startAdornment: (
                                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}

                                                                            >
                                                                                {CalenderSVG}
                                                                            </Box>
                                                                        ),
                                                                        sx: {
                                                                            backgroundColor: "#E7E7EB",
                                                                            height: "40px",
                                                                            width: { xs: "100%", md: "100%" },
                                                                            borderRadius: "3px",
                                                                        },
                                                                    }}
                                                                />
                                                            </Grid>

                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Target Closure Date</Typography>
                                                                <TextField
                                                                    type="date"
                                                                    name="TargetClosureDate"
                                                                    // onBlur={handleBlur}
                                                                    onChange={handleInputChange}
                                                                    value={filters.TargetClosureDate ? new Date(filters.TargetClosureDate).toISOString().split('T')[0] : ""}
                                                                    onClick={(e) => {
                                                                        // Trigger the native date picker
                                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                                        if (input) input.showPicker?.();
                                                                    }}
                                                                    fullWidth
                                                                    sx={{
                                                                        // ...rightTextStyle,
                                                                        "& input::-webkit-calendar-picker-indicator": {
                                                                            display: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& input[type='date']": {
                                                                            appearance: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& .MuiOutlinedInput-root": {
                                                                            "& fieldset": {
                                                                                border: "none",
                                                                            },
                                                                        },
                                                                    }}
                                                                    InputProps={{
                                                                        startAdornment: (
                                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}

                                                                            >
                                                                                {CalenderSVG}
                                                                            </Box>
                                                                        ),
                                                                        sx: {
                                                                            backgroundColor: "#E7E7EB",
                                                                            height: "40px",
                                                                            width: { xs: "100%", md: "100%" },
                                                                            borderRadius: "3px",
                                                                        },
                                                                    }}
                                                                />
                                                            </Grid>

                                                            <Grid size={12}>
                                                                <Box
                                                                    sx={{
                                                                        display: "flex",
                                                                        justifyContent: "flex-end",
                                                                        alignItems: "center",
                                                                    }}
                                                                >
                                                                    <Button onClick={handleReset}
                                                                        sx={{
                                                                            textTransform: "none",
                                                                            color: "#F15744",
                                                                            fontSize: "14px",
                                                                            lineHeight: "100%",
                                                                            fontFamily: "Outfit, sans-serif",
                                                                            borderRadius: "3px",
                                                                            fontWeight: 500,
                                                                            px: "20px",
                                                                            py: "15px",
                                                                        }}>
                                                                        Reset
                                                                    </Button>
                                                                    <Button
                                                                        type="button"
                                                                        onClick={handleApplyFilters}
                                                                        variant="contained"
                                                                        sx={{
                                                                            backgroundColor: "#2563EB",
                                                                            borderRadius: "3px",
                                                                            textTransform: "none",
                                                                            fontWeight: 500,
                                                                            fontSize: "14px",
                                                                            lineHeight: "100%",
                                                                            fontFamily: "Outfit, sans-serif",
                                                                            color: "#FFFFFF",
                                                                            px: "20px",
                                                                            py: "15px",
                                                                        }}
                                                                    >
                                                                        Apply
                                                                    </Button>
                                                                </Box>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </ClickAwayListener>
                                            </Paper>
                                        </Grow>
                                    )}
                                </Popper>

                                <IconButton onClick={() => {
                                    trigger(filters);
                                }}>
                                    <LuRefreshCw size={20} color="#48535B" />
                                </IconButton>

                                {/* <IconButton onClick={() => trigger(filters)}>
                                    <LuRefreshCw size={20} color="#48535B" />
                                </IconButton> */}
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid size={12}>
                    <TableComponent
                        data={ehsFindingList?.data || []}
                        isLoading={isFetching}
                        columns={columns}
                        order={sortOrder}
                        orderBy={sortBy}
                        createSortHandler={handleRequestSort}
                    />
                    <Menu
                        anchorEl={menuAnchorEl}
                        open={Boolean(menuAnchorEl)}
                        onClose={handleMenuClose}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                        PaperProps={{
                            elevation: 0,
                            sx: {
                                mt: 1,
                                borderRadius: "3px",
                                minWidth: 140,
                                border: "1px solid #E5E9EB",
                                boxShadow: "0px 10px 30px rgba(15, 23, 42, 0.12)",
                                overflow: "hidden",
                            },
                        }}
                        MenuListProps={{
                            sx: {
                                py: 0.5,
                            },
                        }}
                    >
                        {viewAccess && <MenuItem
                            onClick={handleMenuView}
                            sx={{
                                fontFamily: "Outfit, sans-serif",
                                fontSize: "14px",
                                fontWeight: 400,
                                px: 1.5,
                                py: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                color: "#111827",
                                "&:hover": {
                                    backgroundColor: "#F6F8F9",
                                },
                            }}
                        >
                            <LuEye size={16} style={{ color: "#4B5563" }} />
                            <span>View</span>
                        </MenuItem>
                        }
                        {editAccess &&
                            <MenuItem
                                onClick={handleMenuEdit}
                                sx={{
                                    fontFamily: "Outfit, sans-serif",
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    px: 1.5,
                                    py: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    color: "#111827",
                                    "&:hover": {
                                        backgroundColor: "#F6F8F9",
                                    },
                                }}
                            >
                                <EditIcon style={{ width: 14, height: 14 }} />
                                <span>Edit</span>
                            </MenuItem>
                        }

                        {deleteAccess &&
                            <MenuItem
                                onClick={handleMenuDelete}
                                sx={{
                                    fontFamily: "Outfit, sans-serif",
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    px: 1.5,
                                    py: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    color: "#111827",
                                    "&:hover": {
                                        backgroundColor: "#F6F8F9",
                                    },
                                }}
                            >
                                <MdDeleteOutline style={{ width: 14, height: 14 }} />
                                <span>Delete</span>
                            </MenuItem>
                        }
                    </Menu>

                    <CustomPagination
                        totalCount={ehsFindingList?.totalRecords || 0}
                        pageSize={pageSize}
                        page={pageNumber}
                        onPageChange={handlePageChange}
                        setCurrentPage={setPageNumber}
                        onPageSizeChange={handlePageSizeChange}
                    />

                </Grid>
            </Grid >

            {isAddEHS && <AddEHSFinding open={isAddEHS} handleClose={handleAddEHS} />}
            {isEdit && <EditEHSFinding open={isEdit} handleClose={handleEditEHSClose} rowData={selectedData} />}
            {isView && <ViewEHSFinding open={isView} handleClose={handleViewClose} rowData={viewData} />}
            {isDelete && <DeleteEHSFinding open={isDelete} handleClose={handleDeleteClose} rowData={deleteData} />}
            <ImageSlider open={show} handleClose={handlePhotographClose} files={files} title={title} />
        </>
    );
}

export default EHSFindingTracker;
